<template>
  <div class="pay-page">
    <div v-if="loading" class="loading-spinner">
      <div class="spinner"></div>
    </div>

    <div v-else-if="raffle.status === 'ACTIVE'" class="raffle-ready" ref="successStage">
      <div class="ready-icon-bubble">
        <i class="material-icons">check_circle</i>
      </div>
      <p class="ready-eyebrow">Pagamento confirmado</p>
      <h1>Sua campanha está no ar!</h1>
      <p class="ready-sub">
        Parabéns! Sua rifa já está pronta para receber participantes e vendas.
      </p>

      <!-- Card de Divulgação Imediata -->
      <div class="ready-share-card">
        <label class="ready-share-label">Link Oficial da sua Rifa:</label>
        <div class="ready-link-row">
          <input
            type="text"
            readonly
            class="ready-link-input"
            :value="campaignPublicUrl"
            @focus="$event.target.select()"
          />
          <button type="button" class="btn-copy-link" @click="copyCampaignLink">
            <i class="material-icons text-sm me-1">content_copy</i>
            {{ linkCopied ? "Copiado!" : "Copiar Link" }}
          </button>
        </div>

        <div class="ready-action-grid">
          <!-- Compartilhar no WhatsApp -->
          <button type="button" class="btn-action-wa" @click="shareOnWhatsapp">
            <i class="material-icons">chat</i>
            <span>Compartilhar no WhatsApp</span>
          </button>

          <!-- Baixar QR Code -->
          <button type="button" class="btn-action-qr-download" @click="showQrCodeModal = true">
            <i class="material-icons">qr_code_2</i>
            <span>Baixar QR Code (HD)</span>
          </button>
        </div>
      </div>

      <div class="ready-footer-actions">
        <button type="button" class="btn-secondary-link" @click="goToDashboard">
          Ir para minhas campanhas
          <i class="material-icons text-sm ms-1">arrow_forward</i>
        </button>
      </div>

      <!-- Modal de QR Code Oficial -->
      <CampaignQrCodeModal
        v-if="showQrCodeModal"
        :is-open="showQrCodeModal"
        :campaign="raffle"
        :user="user"
        @close="showQrCodeModal = false"
      />
    </div>

    <div v-else class="pay-stack">
      <header class="pay-header">
        <p class="pay-eyebrow">Campanha criada</p>
        <h1>{{ raffle.name || "Sua campanha" }}</h1>
        <p class="pay-sub">
          Agora é só pagar a taxa de publicação pelo Pix para ativar.
        </p>
      </header>

      <PixBankNotice v-if="!isExpired" />

      <!-- Card 1: pagamento -->
      <section class="pay-card" :class="{ expired: isExpired }">
        <template v-if="!isExpired">
          <div class="qr-wrap">
            <QrcodeVue
              class="qr-svg"
              :value="raffle.qr_code"
              :size="200"
              :level="level"
              :render-as="renderAs"
            />
          </div>

          <h2 class="pay-amount">
            Pague {{ feeLabel || "a taxa" }} via Pix
          </h2>
          <p class="pay-hint">
            Copie o código ou use a câmera do banco para ler o QR Code.
          </p>

          <div class="pix-field">
            <input
              type="text"
              readonly
              class="pix-input"
              :value="raffle.qr_code"
              @focus="$event.target.select()"
            />
          </div>

          <button type="button" class="btn-brand btn-block" @click="copyPixCode">
            {{ copied ? "Código copiado!" : "Copiar código Pix" }}
          </button>

          <div class="timer-box" :class="{ urgent: remainingMs != null && remainingMs < 30 * 60 * 1000 }">
            <span class="timer-icon" aria-hidden="true">⏱</span>
            <span>
              Código expira em
              <strong>{{ countdownText }}</strong>
            </span>
          </div>
        </template>

        <template v-else>
          <h2 class="pay-amount expired-title">Tempo esgotado</h2>
          <p class="pay-hint">
            O prazo de 6 horas acabou e esta campanha foi cancelada.
            Crie novamente para gerar um novo Pix.
          </p>
          <button type="button" class="btn-brand btn-block" @click="goToDashboard">
            Voltar para campanhas
          </button>
        </template>
      </section>

      <!-- Card 2: etapas -->
      <section class="pay-card steps-card" v-if="!isExpired">
        <h3 class="card-title">Etapas</h3>
        <ol class="steps">
          <li class="step">
            <span class="step-num">1</span>
            <div>
              <strong>Acesse seu banco</strong>
              <p>Abra o app do seu banco — é rapidinho.</p>
            </div>
          </li>
          <li class="step">
            <span class="step-num">2</span>
            <div>
              <strong>Escolha a opção Pix</strong>
              <p>Selecione “Pix Copia e Cola” ou “Ler QR Code”.</p>
            </div>
          </li>
          <li class="step">
            <span class="step-num">3</span>
            <div>
              <strong>Conclua o pagamento</strong>
              <p>Cole o código ou leia o QR, confirme os dados e pronto!</p>
            </div>
          </li>
        </ol>
      </section>

      <!-- Card 3: resumo -->
      <section class="pay-card summary-card" v-if="!isExpired">
        <h3 class="card-title">Resumo da compra</h3>
        <div class="summary-row">
          <span>Campanha</span>
          <span class="summary-val">{{ raffle.name || "—" }}</span>
        </div>
        <div class="summary-row">
          <span>Pagamento Pix</span>
          <span class="summary-val">{{ feeLabel || "—" }}</span>
        </div>
        <div class="summary-row total">
          <span>Total</span>
          <span>{{ feeLabel || "—" }}</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import confetti from "canvas-confetti";
import PixBankNotice from "@/components/PixBankNotice.vue";
import RafflesHttp from "../../../http/RafflesHttp";
import CampaignQrCodeModal from "../RafflesList/components/CampaignQrCodeModal.vue";
import { buildCampaignPublicUrl } from "@/store/config.js";

export default {
  name: "PaymentPage",
  components: { QrcodeVue, CampaignQrCodeModal, PixBankNotice },
  data() {
    return {
      level: "M",
      renderAs: "svg",
      raffle: {},
      loading: true,
      user: null,
      firebaseEventSource: null,
      countdownTimer: null,
      nowTs: Date.now(),
      copied: false,
      copyResetTimer: null,
      confettiFired: false,
      linkCopied: false,
      showQrCodeModal: false,
    };
  },
  watch: {
    "raffle.status"(status) {
      if (status === "ACTIVE") {
        this.$nextTick(() => this.celebratePayment());
      }
    },
  },
  computed: {
    dueDateMs() {
      if (!this.raffle || !this.raffle.due_date) return null;
      const raw = String(this.raffle.due_date).trim();
      const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
      const ms = new Date(normalized).getTime();
      return Number.isNaN(ms) ? null : ms;
    },
    remainingMs() {
      if (this.dueDateMs == null) return null;
      return this.dueDateMs - this.nowTs;
    },
    isExpired() {
      if (this.remainingMs == null) return false;
      return this.remainingMs <= 0;
    },
    countdownText() {
      if (this.remainingMs == null) return "—";
      if (this.remainingMs <= 0) return "expirado";
      const totalSec = Math.floor(this.remainingMs / 1000);
      const hours = Math.floor(totalSec / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;
      if (hours > 0) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
    feeLabel() {
      const final = Number(this.raffle.publication_fee_final);
      const base = Number(this.raffle.publication_fee);
      const fee = final > 0 ? final : base > 0 ? base : null;
      if (fee == null) return null;
      return fee.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    campaignPublicUrl() {
      if (!this.raffle?.slug) return "";
      return buildCampaignPublicUrl(this.raffle.slug, this.user);
    },
  },
  methods: {
    onCopiedOk() {
      this.copied = true;
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => {
        this.copied = false;
      }, 2500);
      this.$toast.add({
        severity: "success",
        summary: "Copiado!",
        detail: "Cole no app do seu banco em Pix Copia e Cola.",
        life: 3000,
      });
    },
    copyPixCode() {
      if (!this.raffle.qr_code) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(this.raffle.qr_code)
          .then(() => this.onCopiedOk())
          .catch(() => this.manualCopyFallback());
      } else {
        this.manualCopyFallback();
      }
    },
    manualCopyFallback() {
      const textArea = document.createElement("textarea");
      textArea.value = this.raffle.qr_code;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const successful = document.execCommand("copy");
        if (successful) this.onCopiedOk();
        else throw new Error("Falha ao copiar");
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro!",
          detail: "Não foi possível copiar. Selecione o código e copie manualmente.",
          life: 3000,
        });
      } finally {
        document.body.removeChild(textArea);
      }
    },
    async getRaffle() {
      const raffle = await RafflesHttp.getRaffle(this.$route.params.slug);
      this.raffle = raffle.data;
    },
    startPolling() {
      if (!this.raffle || !this.raffle.id) return;

      const firebaseUrl = `https://rifa365-6ee07-default-rtdb.firebaseio.com/campaign_status/${this.raffle.id}.json`;
      this.firebaseEventSource = new EventSource(firebaseUrl);

      this.firebaseEventSource.addEventListener("put", async (e) => {
        try {
          const eventData = JSON.parse(e.data);
          if (eventData && eventData.data && eventData.data.status === "ACTIVE") {
            this.raffle.status = "ACTIVE";
            this.$toast.add({
              severity: "success",
              summary: "Sucesso!",
              detail: "Pagamento reconhecido em tempo real!",
              life: 3000,
            });
            this.stopPolling();
          }
        } catch (error) {
          console.error("Erro ao processar o evento do Firebase:", error);
        }
      });

      this.firebaseEventSource.onerror = (error) => {
        console.warn("Conexão EventSource do Firebase caiu ou falhou.", error);
      };
    },
    stopPolling() {
      if (this.firebaseEventSource) {
        this.firebaseEventSource.close();
        this.firebaseEventSource = null;
      }
    },
    goToDashboard() {
      if (this.user && this.user.role === "ADMIN") {
        this.$router.push("/");
      } else {
        this.$router.push("/raffles");
      }
    },
    async copyCampaignLink() {
      if (!this.campaignPublicUrl) return;
      try {
        await navigator.clipboard.writeText(this.campaignPublicUrl);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2500);
        this.$toast.add({
          severity: "success",
          summary: "Link Copiado!",
          detail: "O link da sua campanha foi copiado para a área de transferência.",
          life: 3000,
        });
      } catch (err) {
        console.error("Erro ao copiar link:", err);
      }
    },
    shareOnWhatsapp() {
      if (!this.campaignPublicUrl) return;
      const campName = this.raffle.name || "minha rifa";
      const msg = `🍀 Participe da minha rifa: *${campName}*!\n🏆 Concorra a prêmios incríveis!\n👉 Garanta suas cotas no link:\n${this.campaignPublicUrl}`;
      const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
    },
    celebratePayment() {
      if (this.confettiFired) return;
      this.confettiFired = true;

      const stage = this.$refs.successStage;
      const rect = stage?.getBoundingClientRect();
      const origin = rect
        ? {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          }
        : { x: 0.5, y: 0.4 };

      confetti({
        particleCount: 120,
        spread: 70,
        origin,
        colors: ["#e41b80", "#6610f2", "#198754", "#fbbf24", "#0d6efd"],
      });
      setTimeout(() => {
        confetti({
          particleCount: 70,
          angle: 60,
          spread: 55,
          origin: { x: 0.2, y: 0.7 },
        });
        confetti({
          particleCount: 70,
          angle: 120,
          spread: 55,
          origin: { x: 0.9, y: 0.7 },
        });
      }, 220);
    },
    startCountdown() {
      this.stopCountdown();
      this.nowTs = Date.now();
      this.countdownTimer = setInterval(() => {
        this.nowTs = Date.now();
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
  },
  mounted() {
    this.user = this.$store.getters.user;
    this.getRaffle().finally(() => {
      this.loading = false;
      this.startCountdown();
      if (this.raffle.status === "ACTIVE") {
        this.$nextTick(() => this.celebratePayment());
      } else if (!this.isExpired) {
        this.startPolling();
      }
    });
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopCountdown();
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
  },
};
</script>

<style scoped lang="scss">
$brand: #e41b80;
$brand-dark: #c21668;
$text: #1f2937;
$muted: #6b7280;
$border: #e5e7eb;
$bg: #f3f4f6;

.pay-page {
  min-height: 70vh;
  padding: 20px 16px 40px;
  background: $bg;
  animation: fadeIn 0.5s ease;
}

.pay-stack {
  max-width: 440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pay-header {
  text-align: center;
  padding: 8px 4px 4px;

  .pay-eyebrow {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $brand;
  }

  h1 {
    margin: 0 0 6px;
    font-size: 22px;
    font-weight: 700;
    color: $text;
    line-height: 1.25;
  }

  .pay-sub {
    margin: 0;
    font-size: 14px;
    color: $muted;
    line-height: 1.4;
  }
}

.pay-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  padding: 22px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  text-align: center;

  &.expired {
    border-color: #fca5a5;
  }
}

.qr-wrap {
  display: inline-flex;
  padding: 12px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  margin-bottom: 16px;
}

.pay-amount {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: $text;
}

.expired-title {
  color: #b91c1c;
}

.pay-hint {
  margin: 0 0 16px;
  font-size: 13px;
  color: $muted;
  line-height: 1.45;
}

.pix-field {
  margin-bottom: 12px;
}

.pix-input {
  width: 100%;
  box-sizing: border-box;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: $text;
  background: #f9fafb;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 12px 14px;
  text-overflow: ellipsis;
}

.btn-brand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, $brand 0%, $brand-dark 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.15s ease;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
}

.btn-block {
  width: 100%;
}

.timer-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 12px;
  border: 1px solid $border;
  border-radius: 10px;
  font-size: 13px;
  color: $muted;
  background: #fafafa;

  strong {
    color: $text;
    font-variant-numeric: tabular-nums;
  }

  &.urgent {
    border-color: #fbbf24;
    background: #fffbeb;
    color: #92400e;

    strong {
      color: #b45309;
    }
  }
}

.timer-icon {
  font-size: 14px;
}

.card-title {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 700;
  color: $text;
  text-align: left;
}

.steps {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: left;
}

.step {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px dashed $border;
  }

  strong {
    display: block;
    font-size: 14px;
    color: $text;
    margin-bottom: 2px;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: $muted;
    line-height: 1.4;
  }
}

.step-num {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba($brand, 0.12);
  color: $brand;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.summary-card {
  text-align: left;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: $muted;
  border-bottom: 1px solid $border;

  .summary-val {
    color: $text;
    text-align: right;
    word-break: break-word;
  }

  &.total {
    border-bottom: none;
    padding-top: 14px;
    font-weight: 700;
    color: $text;
    font-size: 15px;
  }
}

.raffle-ready {
  max-width: 480px;
  margin: 30px auto;
  text-align: center;
  padding: 36px 20px 30px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);

  .ready-icon-bubble {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #ecfdf5;
    color: #10b981;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);

    i {
      font-size: 38px;
    }
  }

  .ready-eyebrow {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #059669;
  }

  h1 {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
  }

  .ready-sub {
    margin: 0 0 20px;
    font-size: 14px;
    color: #64748b;
    line-height: 1.45;
  }
}

.ready-share-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: left;
}

.ready-share-label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
  display: block;
}

.ready-link-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.ready-link-input {
  flex: 1;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  outline: none;
  min-width: 0;

  &:focus {
    border-color: #6366f1;
  }
}

.btn-copy-link {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.4);
  }
}

.ready-action-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
}

.btn-action-wa {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.3);

  i {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  }
}

.btn-action-qr-download {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.3);

  i {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  }
}

.ready-footer-actions {
  margin-top: 10px;
}

.btn-secondary-link {
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #0f172a;
    text-decoration: underline;
  }
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #fce7f3;
  border-top-color: $brand;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (min-width: 768px) {
  .pay-page {
    padding: 28px 24px 48px;
  }

  .pay-header h1 {
    font-size: 26px;
  }
}
</style>
